import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { useAuth } from '@astro/auth'
import {
  unwrapReviewList, unwrapReviewSummary, findMyReview,
  computeBreakdownFromList, computeAverageFromList,
} from '../components/sections/reviews/reviewsData'

const AGGREGATE_SAMPLE_SIZE = 50

/**
 * Average rating + total + per-star breakdown for one reviewable, sourced
 * primarily from GET /reviews (which is public and reliably works) rather
 * than GET /reviews/summary.
 *
 * GET /reviews/summary is guarded by the upstream's legacy `auth:api` (JWT)
 * middleware — a DIFFERENT auth system from the Sanctum session this app's
 * BFF issues (verified live: it 401s with "Unauthenticated" even for a
 * signed-in user, since our session never holds that JWT). Still called
 * here best-effort — if a deployment ever fixes the guard, its exact
 * numbers are used automatically — but its failure must never blank out
 * real review data, so List Reviews' own `meta.total` (exact) plus the
 * loaded sample's computed average/breakdown (labelled "loaded" when the
 * sample doesn't cover every review) are always the fallback.
 */
export function useReviewsAggregate(reviewableType, reviewableId) {
  const { user } = useAuth()
  const enabled = Boolean(reviewableType) && Boolean(reviewableId)

  const sampleQuery = useQuery({
    queryKey: ['reviews-list', reviewableType, reviewableId, { sortBy: 'recent', page: 1, perPage: AGGREGATE_SAMPLE_SIZE }],
    queryFn: () =>
      api.reviews.list({
        reviewable_type: reviewableType,
        reviewable_id: reviewableId,
        sort_by: 'recent',
        per_page: AGGREGATE_SAMPLE_SIZE,
      }),
    enabled,
    select: unwrapReviewList,
  })

  const summaryQuery = useQuery({
    queryKey: ['reviews-summary', reviewableType, reviewableId],
    queryFn: () => api.reviews.summary({ reviewable_type: reviewableType, reviewable_id: reviewableId }),
    enabled,
    retry: false,
    select: unwrapReviewSummary,
  })

  const items = sampleQuery.data?.items ?? []
  const sampleTotal = sampleQuery.data?.total ?? 0
  const apiSummary = summaryQuery.data && summaryQuery.data.totalReviews > 0 ? summaryQuery.data : null

  const totalReviews = apiSummary?.totalReviews ?? sampleTotal
  const averageRating = apiSummary?.averageRating ?? computeAverageFromList(items)
  const breakdown = apiSummary?.breakdown ?? (items.length > 0 ? computeBreakdownFromList(items) : null)
  const breakdownBasis = apiSummary?.breakdown ? 'exact' : (sampleTotal > items.length ? 'loaded' : 'exact')

  return {
    summary: { totalReviews, averageRating, breakdown, breakdownBasis },
    myReview: findMyReview(items, user?.id),
    isPending: sampleQuery.isPending,
    isError: sampleQuery.isError,
    refetch: sampleQuery.refetch,
  }
}

/**
 * Lightweight live rating + review count for MANY reviewables at once —
 * card grids (similar astrologers, listings) that can't reasonably show a
 * static per-record `rating`/`reviews_count` field, since the astrologer
 * resource doesn't keep those in sync upstream (verified null even for
 * astrologers with real reviews — see useReviewsAggregate). Skips the
 * always-401 summary call per card; a `per_page` sample from GET /reviews
 * is enough for a badge. Returns a map keyed by id: { averageRating,
 * totalReviews, isPending }.
 */
export function useReviewSummaries(reviewableType, ids = []) {
  const uniqueIds = [...new Set(ids.filter((id) => id != null))]
  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['reviews-list', reviewableType, id, { sortBy: 'recent', page: 1, perPage: AGGREGATE_SAMPLE_SIZE }],
      queryFn: () =>
        api.reviews.list({
          reviewable_type: reviewableType,
          reviewable_id: id,
          sort_by: 'recent',
          per_page: AGGREGATE_SAMPLE_SIZE,
        }),
      staleTime: 60_000,
      select: unwrapReviewList,
    })),
  })

  return Object.fromEntries(
    uniqueIds.map((id, i) => {
      const q = queries[i]
      const items = q.data?.items ?? []
      return [
        id,
        {
          averageRating: computeAverageFromList(items),
          totalReviews: q.data?.total ?? 0,
          isPending: q.isPending,
        },
      ]
    }),
  )
}

/** GET /reviews — paginated, sortable/filterable list for one reviewable. */
export function useReviewsList(reviewableType, reviewableId, { sortBy, rating, page = 1, perPage = 10 } = {}) {
  const query = useQuery({
    queryKey: ['reviews-list', reviewableType, reviewableId, { sortBy, rating, page, perPage }],
    queryFn: () =>
      api.reviews.list({
        reviewable_type: reviewableType,
        reviewable_id: reviewableId,
        sort_by: sortBy,
        rating: rating || undefined,
        page,
        per_page: perPage,
      }),
    enabled: Boolean(reviewableType) && Boolean(reviewableId),
    placeholderData: (prev) => prev,
    select: unwrapReviewList,
  })
  return {
    items: query.data?.items ?? [],
    currentPage: query.data?.currentPage ?? 1,
    lastPage: query.data?.lastPage ?? 1,
    total: query.data?.total ?? 0,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * Whether — and what — the signed-in user has already posted for this
 * reviewable. Runs its own unfiltered, unpaginated-ish read (largest page
 * size) so it stays correct regardless of whatever sort/filter the visible
 * list is currently showing. There's no dedicated "my review" endpoint, so
 * this is the only way to drive the Write-vs-Already-reviewed affordance.
 */
export function useMyReview(reviewableType, reviewableId) {
  const { user, status } = useAuth()
  const query = useQuery({
    queryKey: ['reviews-mine', reviewableType, reviewableId, user?.id],
    queryFn: () =>
      api.reviews.list({
        reviewable_type: reviewableType,
        reviewable_id: reviewableId,
        per_page: 50,
      }),
    enabled: status === 'authenticated' && Boolean(user?.id) && Boolean(reviewableType) && Boolean(reviewableId),
    select: (res) => findMyReview(unwrapReviewList(res).items, user?.id),
  })
  return {
    myReview: query.data ?? null,
    isPending: status === 'loading' || (status === 'authenticated' && query.isPending),
    refetch: query.refetch,
  }
}

/** POST /reviews — refreshes every cached view of this reviewable on success. */
export function useSubmitReview(reviewableType, reviewableId) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload) =>
      api.reviews.create({
        reviewable_type: reviewableType,
        reviewable_id: reviewableId,
        ...payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews-list', reviewableType, reviewableId] })
      queryClient.invalidateQueries({ queryKey: ['reviews-summary', reviewableType, reviewableId] })
      queryClient.invalidateQueries({ queryKey: ['reviews-mine', reviewableType, reviewableId] })
    },
  })
}
